/**
 * Path Memory: level params and seeded path generation (spec Game 13).
 */

export interface PathMemoryParams {
  gridRows: number;
  gridCols: number;
  pathLength: number;
  displayMs: number;
  showCount: number;
  allowBacktrack: boolean;
}

/**
 * Level params per spec:
 * 1–2: 3×3, 3 steps, 800ms, path shown twice
 * 3–4: 3×3, 4 steps, 800ms, once
 * 5–6: 4×4, 5–6 steps, 700ms, once
 * 7–8: 4×4, 7–8 steps, 600ms, once, path can backtrack
 * 9–10: 5×5, 9–12 steps, 500ms, once
 */
export function getPathMemoryParams(level: number): PathMemoryParams {
  const clamped = Math.max(1, Math.min(10, Math.floor(level)));
  if (clamped <= 2) {
    return {
      gridRows: 3,
      gridCols: 3,
      pathLength: 3,
      displayMs: 900,
      showCount: 2,
      allowBacktrack: false,
    };
  }
  if (clamped <= 4) {
    return {
      gridRows: 3,
      gridCols: 3,
      pathLength: 4,
      displayMs: 900,
      showCount: 1,
      allowBacktrack: false,
    };
  }
  if (clamped <= 6) {
    const pathLength = clamped === 5 ? 5 : 6;
    return {
      gridRows: 4,
      gridCols: 4,
      pathLength,
      displayMs: 780,
      showCount: 1,
      allowBacktrack: false,
    };
  }
  if (clamped <= 8) {
    const pathLength = clamped === 7 ? 7 : 8;
    return {
      gridRows: 4,
      gridCols: 4,
      pathLength,
      displayMs: 660,
      showCount: 1,
      allowBacktrack: true,
    };
  }
  return {
    gridRows: 5,
    gridCols: 5,
    pathLength: clamped === 9 ? 9 : 11,
    displayMs: 560,
    showCount: 1,
    allowBacktrack: true,
  };
}

/** Seeded RNG in [0, 1) for deterministic paths */
export function seededRandom(seed: string): () => number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (h << 5) - h + seed.charCodeAt(i);
    h |= 0;
  }
  return () => {
    h = (h * 1103515245 + 12345) & 0x7fffffff;
    return h / 0x7fffffff;
  };
}

/**
 * Random walk on grid. Cell index = row * cols + col.
 * For allowBacktrack false: never revisit a cell.
 * For allowBacktrack true: can step back to previous cell.
 */
export function generatePath(
  rows: number,
  cols: number,
  pathLength: number,
  allowBacktrack: boolean,
  seed: string
): number[] {
  const rng = seededRandom(seed);
  const path: number[] = [];
  const total = rows * cols;
  if (total === 0 || pathLength <= 0) return path;

  const startRow = Math.floor(rng() * rows);
  const startCol = Math.floor(rng() * cols);
  let cell = startRow * cols + startCol;
  path.push(cell);

  const visited = new Set<number>();
  visited.add(cell);

  while (path.length < pathLength) {
    const r = Math.floor(cell / cols);
    const c = cell % cols;
    const neighbors: number[] = [];
    if (r > 0) neighbors.push((r - 1) * cols + c);
    if (r < rows - 1) neighbors.push((r + 1) * cols + c);
    if (c > 0) neighbors.push(r * cols + (c - 1));
    if (c < cols - 1) neighbors.push(r * cols + (c + 1));

    const allowed = allowBacktrack
      ? neighbors
      : neighbors.filter((n) => !visited.has(n));

    if (allowed.length === 0) break;
    const next = allowed[Math.floor(rng() * allowed.length)];
    path.push(next);
    visited.add(next);
    cell = next;
  }

  return path;
}

/**
 * Scoring helper for a single Path Memory round. Compares the shown path to
 * the path the player tapped in order and returns the metrics used to drive
 * XP / adaptive difficulty:
 *   - `accuracy`: fraction of cells tapped at the right position.
 *   - `furthestCorrectStep`: 1-based index of the latest tap whose cell
 *     matched the correct path. Represents how far the player got, not the
 *     length of the initial correct prefix (non-contiguous matches count).
 *   - `firstErrorIndex`: 1-based index of the first wrong tap, or 0 if none.
 *   - `errorCount`: cells not correctly tapped (including missing ones).
 *
 * The comparison is strictly index-based: a player who taps the right cells
 * in the wrong order scores as many errors as positional mismatches. A user
 * path shorter than the correct path counts the missing cells as errors.
 */
export interface PathMemoryRoundMetrics {
  accuracy: number;
  furthestCorrectStep: number;
  firstErrorIndex: number;
  errorCount: number;
}

export function computePathMemoryRoundMetrics(
  correctPath: number[],
  userPath: number[],
): PathMemoryRoundMetrics {
  const pathLength = correctPath.length;
  if (pathLength === 0) {
    return { accuracy: 1, furthestCorrectStep: 0, firstErrorIndex: 0, errorCount: 0 };
  }
  let furthestCorrectStep = 0;
  let firstErrorIndex = 0;
  let correctCount = 0;
  for (let i = 0; i < pathLength && i < userPath.length; i++) {
    if (userPath[i] === correctPath[i]) {
      correctCount++;
      furthestCorrectStep = i + 1;
    } else if (firstErrorIndex === 0) {
      firstErrorIndex = i + 1;
    }
  }
  const errorCount = pathLength - correctCount;
  const accuracy = correctCount / pathLength;
  return { accuracy, furthestCorrectStep, firstErrorIndex, errorCount };
}
